import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectAdditionalSaleDataByName,
  selectAdditionalSalesConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectConfig } from '../../SubscriptionForm/SubscriptionFormSelectors'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { useSnackbarNotification } from '@/shared/tools/notifications/useSnackbarNotification'

const useCreationColumn = ({ saleType, updateLeadDispatch }) => {
  const { lang, country } = useSelector(selectAuthData)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    additional_sale_create_callback_url: additionalSaleCreateCallbackUrl,
    additional_sale_re_sync_url: additionalSaleReSyncUrl,
  } = useSelector(selectPageConfigurationData)
  const { contactId: id } = useSelector(selectConfig)
  const saleDataState = useSelector(selectAdditionalSaleDataByName(saleType))
  const showNotification = useSnackbarNotification()
  const allowedRetrySalesList = [
    'leadAssistance',
    'leadCarbonOffset',
    'leadMonConseillerPerso',
    'leadProtected',
    'leadVoltalisBizdev',
    'leadNeat',
    'leadAxaBillProtection',
  ]
  const additionalSalesConfig = useSelector(selectAdditionalSalesConfig)
  const creationDate = saleDataState?.created_at
  const retriedSales = additionalSalesConfig?.retriedSales || []
  const blockedSales = additionalSalesConfig?.blockedSales || []
  const defaultBlockedSaleMessageKey = 'additionalSales:errors.sale_already_sold_to_customer'
  const blockedSaleMessageKeys = {
    DUPLICATE_GROUP_SALE: 'additionalSales:errors.sale_already_has_a_sale_of_the_same_group',
    MISSING_PREREQUISITE_SALE: 'additionalSales:errors.sale_requires_a_previous_sale',
    RECENT_CONTACT_SALE: 'additionalSales:errors.sale_already_sold_recently',
  }
  const blockedReason = additionalSalesConfig?.blockedSalesReasons?.[saleType]
  const blockedSaleMessageKey = blockedSaleMessageKeys[blockedReason] ?? defaultBlockedSaleMessageKey
  const addressGroupedTypes = ['leadAssistance', 'leadAxaSmartphone']
  const isBlockedByExistingSale = blockedSales.includes(saleType) && !saleDataState?.saleDatabaseId

  let hasBeenRetried = saleDataState?.saleRetried

  if (!hasBeenRetried && addressGroupedTypes.includes(saleType) && saleDataState?.addressId) {
    hasBeenRetried = retriedSales.includes(`${saleType}_${saleDataState.addressId}`)
  }

  if (!hasBeenRetried) {
    hasBeenRetried = retriedSales.includes(saleType)
  }

  const isSaleInRetryList = allowedRetrySalesList.includes(saleType)

  const animationHandler = () => {
    setTimeout(() => {
      updateLeadDispatch({
        progressBarValue: 50,
        state: t('additionalSales:callback_state.in_progress'),
      })
    }, 1750)

    setTimeout(() => {
      updateLeadDispatch({
        progressBarValue: 80,
      })
    }, 3500)
  }

  const fieldLabelKeys = {
    firstname: 'firstname',
    lastname: 'surname',
    phone: 'phone',
    email: 'email',
    iban: 'iban_only',
    zohoId: 'zohoId',
    city: 'city',
    zipcode: 'zipcode',
    streetName: 'street_name',
    effectiveDate: 'effective_date',
  }

  const formatErrorDetails = (errorDetails, messageKey) => {
    if (!errorDetails?.length) return ''
    const labels = errorDetails.map(field =>
      fieldLabelKeys[field] ? t(`additionalSales:list_data.${fieldLabelKeys[field]}`) : field,
    )
    return t(`additionalSales:errors.${messageKey}`, { fields: labels.join(', ') })
  }

  const formatValidationErrors = errors => {
    const entries = Object.entries(errors || {})
    if (!entries.length) return ''
    const messages = entries.map(([field, fieldMessages]) => {
      const [message] = Array.isArray(fieldMessages) ? fieldMessages : []
      return message || (fieldLabelKeys[field] ? t(`additionalSales:list_data.${fieldLabelKeys[field]}`) : field)
    })
    return messages.join(' ')
  }

  const handleErrorResponse = (response, isRetry = false) => {
    const isError = response instanceof Error || response.status >= 400
    const responseData = response.responseData || response
    const errorCode = responseData.errorCode || response.status

    if (isError && errorCode) {
      let saleCreateError
      switch (errorCode) {
        case 500:
          saleCreateError = responseData.error || response.message
          if (isRetry) {
            showNotification({
              type: SNACKBAR_TYPES.ERROR,
              message: t('additionalSales:common.retry_fail'),
            })
            updateLeadDispatch({
              isRetryModalOpen: false,
              isRetrying: false,
              createdInDatabase: false,
              actionsInProgress: false,
              state: '',
            })
            return
          }
          break
        case 400:
          saleCreateError = formatErrorDetails(responseData.errorDetails, 'fields_required')
          break
        case 'FIELDS_INVALID':
          saleCreateError = formatErrorDetails(responseData.errorDetails, 'fields_invalid')
          break
        case 422:
          saleCreateError = formatValidationErrors(response.validationErrors || responseData.errors)
          break
        case 'DUPLICATE_CONTACT_SALE':
          saleCreateError = t('additionalSales:errors.sale_already_sold_to_customer')
          break
        case 'DUPLICATE_GROUP_SALE':
          saleCreateError = t('additionalSales:errors.sale_already_has_a_sale_of_the_same_group')
          break
        case 'MISSING_PREREQUISITE_SALE':
          saleCreateError = t('additionalSales:errors.sale_requires_a_previous_sale')
          break
        case 'RECENT_CONTACT_SALE':
          saleCreateError = t('additionalSales:errors.sale_already_sold_recently')
          break
        case 'DATA_INVALID':
          saleCreateError = responseData.error || response.message
          break
        default:
          saleCreateError = responseData.error_message || responseData.error || response.message
          break
      }
      updateLeadDispatch({
        createdInDatabase: false,
        actionsInProgress: false,
        state: '',
        saleCreateError,
        ...(isRetry ? { isRetryModalOpen: false, isRetrying: false } : {}),
      })
    } else {
      if (isRetry) {
        updateLeadDispatch({
          isRetryModalOpen: false,
          isRetrying: false,
          createdInDatabase: false,
          actionsInProgress: false,
          canRetry: false,
          saleDatabaseId: null,
          state: '',
          statusCode: '',
          status: '',
          attributes: null,
          infoAttributes: null,
          code_status: null,
          document_status: null,
          passcode_required: null,
          passcode_set_at: null,
          zohoOpportunityId: null,
          zohoContractId: null,
          zohoContractDetailId: null,
          saleRetried: true,
          createdBy: null,
          created_at: new Date().toISOString(),
        })
      } else {
        // The agent on screen just created it: drop the creator of the sale it replaces.
        updateLeadDispatch({
          createdInDatabase: true,
          createdBy: null,
          created_at: new Date().toISOString(),
        })
        animationHandler()
      }
    }
  }

  const sendLeadRequest = async (url, bodyConfig) => {
    const finalUrl = url?.replace('*', id)
    updateLeadDispatch({
      actionsInProgress: true,
      saleCreateError: '',
      state: t('additionalSales:callback_state.start'),
      progressBarValue: 10,
    })
    dispatch({
      type: subscriptionFormActionsV2.CREATE_ADDITIONAL_SALE,
      body: bodyConfig,
      url: finalUrl,
      callback: response => handleErrorResponse(response, false),
    })
  }

  const retryLeadRequest = async url => {
    if (!saleDataState?.saleDatabaseId) {
      showNotification({
        type: SNACKBAR_TYPES.ERROR,
        message: t('additionalSales:common.retry_fail'),
      })
      return
    }
    const finalUrl = url?.replace('*', saleDataState?.saleDatabaseId)
    dispatch({
      type: subscriptionFormActionsV2.RETRY_ADDITIONAL_SALE,
      url: finalUrl,
      callback: response => handleErrorResponse(response, true),
    })
  }

  return {
    sendLeadRequest,
    retryLeadRequest,
    subscriptionId: id,
    additionalSaleReSyncUrl,
    additionalSaleCreateCallbackUrl,
    lang,
    country,
    animationHandler,
    saleDataState,
    hasBeenRetried,
    isBlockedByExistingSale,
    blockedSaleMessageKey,
    isSaleInRetryList,
    creationDate,
    t,
  }
}

export default useCreationColumn
